import { isDarkColorThemeKey } from './darkThemeKeys';
import {
  TABLE_UI_THEME_STORAGE_KEY,
  DEFAULT_TABLE_UI_THEME,
  normalizeTableUiThemeId,
} from './tableUiTheme';
import { applyTableUiTheme } from './tableUiThemeRuntime';
import { getUIConfig } from './appConfig';

/** Current localStorage key for color theme. */
export const COLOR_THEME_STORAGE_KEY = 'qms-ui-theme';

/** Legacy key — migrated once on load. */
export const LEGACY_COLOR_THEME_STORAGE_KEY = 'shippingFMS-theme';

/** Sidebar layout booleans (local cache). */
export const UI_LAYOUT_PREFS_STORAGE_KEY = 'ui-sidebar-layout-prefs';

const DEFAULT_COLOR_THEME = 'qms_professional';

function readLayoutPrefsFromLocalStorage() {
  try {
    const raw = localStorage.getItem(UI_LAYOUT_PREFS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function parseBooleanPref(value, defaultValue = true) {
  if (typeof value === 'boolean') return value;
  return defaultValue;
}

/** Themes shown in picker (active, maintained). */
export const THEME_SWITCHER_IDS = [
  'qms_professional',
  'qms_clarity',
  'qms_dark',
  'corporate_blue',
  'professional_custom',
];

/** @param {Record<string, object>} allThemes — ThemeContext `themes` */
export function getSelectableThemes(allThemes) {
  return Object.fromEntries(
    THEME_SWITCHER_IDS.filter((id) => allThemes[id]).map((id) => [id, allThemes[id]])
  );
}

/**
 * Migrate browser keys after rename.
 */
export function migrateUiPreferenceStorageKeys() {
  try {
    const legacy = localStorage.getItem(LEGACY_COLOR_THEME_STORAGE_KEY);
    const current = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
    if (legacy && !current && legacy.length > 0) {
      localStorage.setItem(COLOR_THEME_STORAGE_KEY, legacy);
      localStorage.removeItem(LEGACY_COLOR_THEME_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {object|null|undefined} source — user.uiPreferences or plain object
 * @returns {{
 *   colorTheme: string,
 *   tableUiTheme: string,
 *   showSidebarGroups: boolean,
 *   showSidebarSearch: boolean,
 *   showSidebarFavorites: boolean,
 * }}
 */
export function parseUiPreferences(source) {
  const raw =
    source && typeof source === 'object' && !Array.isArray(source) ? source : {};
  const localLayout = readLayoutPrefsFromLocalStorage();

  let colorTheme = typeof raw.colorTheme === 'string' ? raw.colorTheme : null;
  let tableUiTheme =
    typeof raw.tableUiTheme === 'string' ? normalizeTableUiThemeId(raw.tableUiTheme) : null;

  try {
    if (!colorTheme) {
      migrateUiPreferenceStorageKeys();
      const stored = localStorage.getItem(COLOR_THEME_STORAGE_KEY);
      if (stored) colorTheme = stored;
    }
    if (!tableUiTheme) {
      const storedTable = localStorage.getItem(TABLE_UI_THEME_STORAGE_KEY);
      if (storedTable) tableUiTheme = normalizeTableUiThemeId(storedTable);
    }
  } catch {
    /* ignore */
  }

  if (!colorTheme) {
    colorTheme = getUIConfig('defaultColorTheme') || DEFAULT_COLOR_THEME;
  }

  if (!tableUiTheme) {
    tableUiTheme = normalizeTableUiThemeId(getUIConfig('defaultTableUiTheme') || DEFAULT_TABLE_UI_THEME);
  }

  const showSidebarGroups = parseBooleanPref(
    raw.showSidebarGroups ?? localLayout.showSidebarGroups,
    true
  );
  const showSidebarSearch = parseBooleanPref(
    raw.showSidebarSearch ?? localLayout.showSidebarSearch,
    true
  );
  const showSidebarFavorites = parseBooleanPref(
    raw.showSidebarFavorites ?? localLayout.showSidebarFavorites,
    true
  );

  return {
    colorTheme,
    tableUiTheme,
    showSidebarGroups,
    showSidebarSearch,
    showSidebarFavorites,
  };
}

/**
 * Read preferences from auth user + localStorage fallback.
 * @param {object|null} user
 */
export function resolveUiPreferences(user) {
  return parseUiPreferences(user?.uiPreferences);
}

/**
 * Persist to localStorage only (no API).
 */
export function persistUiPreferencesLocal(partial = {}) {
  const {
    colorTheme,
    tableUiTheme,
    showSidebarGroups,
    showSidebarSearch,
    showSidebarFavorites,
  } = partial;

  try {
    if (colorTheme) {
      localStorage.setItem(COLOR_THEME_STORAGE_KEY, colorTheme);
      localStorage.removeItem(LEGACY_COLOR_THEME_STORAGE_KEY);
    }
    if (tableUiTheme) {
      localStorage.setItem(TABLE_UI_THEME_STORAGE_KEY, normalizeTableUiThemeId(tableUiTheme));
    }

    const layoutPatch = {};
    if (showSidebarGroups !== undefined) {
      layoutPatch.showSidebarGroups = Boolean(showSidebarGroups);
    }
    if (showSidebarSearch !== undefined) {
      layoutPatch.showSidebarSearch = Boolean(showSidebarSearch);
    }
    if (showSidebarFavorites !== undefined) {
      layoutPatch.showSidebarFavorites = Boolean(showSidebarFavorites);
    }
    if (Object.keys(layoutPatch).length > 0) {
      localStorage.setItem(
        UI_LAYOUT_PREFS_STORAGE_KEY,
        JSON.stringify({
          ...readLayoutPrefsFromLocalStorage(),
          ...layoutPatch,
        })
      );
    }
  } catch {
    /* ignore */
  }

  if (typeof document !== 'undefined' && colorTheme) {
    const root = document.documentElement;
    if (isDarkColorThemeKey(colorTheme)) root.classList.add('dark');
    else root.classList.remove('dark');
  }
}

/**
 * GET /profile/ui-preferences for the logged-in user.
 * @returns {Promise<{ colorTheme: string, tableUiTheme: string }|null>}
 */
export async function fetchUiPreferencesFromServer() {
  const token = localStorage.getItem('authToken');
  if (!token) return null;

  try {
    const { API_BASE_URL } = await import('./apiConfig');
    const res = await fetch(`${API_BASE_URL}/profile/ui-preferences`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return parseUiPreferences(data.uiPreferences);
  } catch {
    return null;
  }
}

/**
 * PATCH /profile/ui-preferences (fire-and-forget).
 */
export async function saveUiPreferencesToServer(partial) {
  const token = localStorage.getItem('authToken');
  if (!token) return null;

  const { API_BASE_URL } = await import('./apiConfig');
  const body = {};
  if (partial.colorTheme !== undefined) body.colorTheme = partial.colorTheme;
  if (partial.tableUiTheme !== undefined) {
    body.tableUiTheme = normalizeTableUiThemeId(partial.tableUiTheme);
  }
  if (partial.showSidebarGroups !== undefined) {
    body.showSidebarGroups = Boolean(partial.showSidebarGroups);
  }
  if (partial.showSidebarSearch !== undefined) {
    body.showSidebarSearch = Boolean(partial.showSidebarSearch);
  }
  if (partial.showSidebarFavorites !== undefined) {
    body.showSidebarFavorites = Boolean(partial.showSidebarFavorites);
  }
  if (Object.keys(body).length === 0) return null;

  try {
    const res = await fetch(`${API_BASE_URL}/profile/ui-preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const storedJson = normalizeUiPreferencesJson(data.uiPreferences);
    const prefs = parseUiPreferences(storedJson);

    applyUiPreferencesToAuthUser(storedJson);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ui-preferences-applied'));
    }
    return prefs;
  } catch {
    return null;
  }
}

/**
 * Merge user.uiPreferences into localStorage after login.
 * @param {object|null} user
 */
export function syncUserUiPreferencesToLocal(user) {
  const prefs = resolveUiPreferences(user);
  persistUiPreferencesLocal(prefs);
  applyTableUiTheme(prefs.tableUiTheme, { persist: true });
  return prefs;
}

/** Canonical uiPreferences object for DB / auth user. */
export function uiPreferencesToJson(prefs) {
  return {
    colorTheme: prefs.colorTheme,
    tableUiTheme: prefs.tableUiTheme,
    showSidebarGroups: prefs.showSidebarGroups,
    showSidebarSearch: prefs.showSidebarSearch,
    showSidebarFavorites: prefs.showSidebarFavorites,
  };
}

/** Merge API/stored JSON with parsed defaults. */
export function normalizeUiPreferencesJson(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return uiPreferencesToJson(parseUiPreferences(raw));
  }
  return uiPreferencesToJson(parseUiPreferences(null));
}

/** Persist full uiPreferences on the logged-in user in localStorage. */
export function applyUiPreferencesToAuthUser(uiPreferencesJson) {
  const json = normalizeUiPreferencesJson(uiPreferencesJson);
  try {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return json;
    const parsed = JSON.parse(savedUser);
    parsed.uiPreferences = json;
    localStorage.setItem('user', JSON.stringify(parsed));
    return json;
  } catch {
    return json;
  }
}

/** Read current user uiPreferences from localStorage (if logged in). */
export function readAuthUserUiPreferencesRaw() {
  try {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return null;
    return JSON.parse(savedUser)?.uiPreferences ?? null;
  } catch {
    return null;
  }
}
